import moment from 'moment'

export const ONBOARDING_CHECK_KEYS = {
  historyStart: 'historyStart',
  dateOrder: 'dateOrder',
  dataVolume: 'dataVolume'
}

export const ONBOARDING_CHECK_STATUSES = {
  ok: 'ok',
  warning: 'warning',
  unknown: 'unknown'
}

export const EXPECTED_HISTORY_YEARS = 6
export const AVERAGE_BOOKING_NIGHTS = 1.5
export const EXPECTED_OCCUPANCY_RATE = 0.5
export const INDUSTRY_MAX_OCCUPANCY_RATE = 0.65
export const MISSING_HISTORY_TOLERANCE_DAYS = 31
export const ALLOWED_GAP_DAYS = 1
export const LOW_VOLUME_RATIO = 0.5
export const HIGH_VOLUME_RATIO = INDUSTRY_MAX_OCCUPANCY_RATE / EXPECTED_OCCUPANCY_RATE

const EMPTY_REPORT_DATE = '0001-01-01T00:00:00'

const DISCARDED_UPLOAD_STATUSES = [
  'Cancelled',
  'Deleted',
  'DeletingStarted',
  'Failed',
  'MigrationStarted',
  'Migrated'
]

function readReportDate(primaryDate, fallbackDate) {
  for (const candidate of [primaryDate, fallbackDate]) {
    if (!candidate || candidate === EMPTY_REPORT_DATE) {
      continue
    }

    const parsedDate = moment(candidate)
    if (parsedDate.isValid()) {
      return parsedDate.startOf('day')
    }
  }

  return null
}

function formatDate(date) {
  return date.format('MMM D YYYY')
}

function formatCount(value) {
  return Math.round(value).toLocaleString('en-US')
}

function pluralize(count, singular) {
  return count === 1 ? `${count} ${singular}` : `${count} ${singular}s`
}

/**
 * Turns upload log rows into the date ranges their data covers, sorted by start date.
 * Reports that were deleted, cancelled or failed do not carry usable data.
 */
export function collectReportRanges(logs = []) {
  return logs
    .filter(log => !DISCARDED_UPLOAD_STATUSES.includes(log.uploadStatus))
    .map(log => {
      const start = readReportDate(log.reportBeginDate, log.beginDate)
      const end = readReportDate(log.reportEndDate, log.endDate)

      if (!start || !end) {
        return null
      }

      return {
        id: log.id,
        start,
        end,
        isReversed: end.isBefore(start),
        coveredDays: Math.max(1, end.diff(start, 'days')),
        bookings: Number(log.numberOfTotalRecords) || 0
      }
    })
    .filter(Boolean)
    .sort((left, right) => left.start.valueOf() - right.start.valueOf())
}

/** Joins ranges that touch or overlap so overlapping reports are not counted twice. */
export function mergeReportRanges(ranges) {
  const mergedRanges = []

  ranges
    .filter(range => !range.isReversed)
    .forEach(range => {
      const lastRange = mergedRanges[mergedRanges.length - 1]

      if (lastRange && range.start.diff(lastRange.end, 'days') <= ALLOWED_GAP_DAYS) {
        if (range.end.isAfter(lastRange.end)) {
          lastRange.end = range.end.clone()
        }

        return
      }

      mergedRanges.push({ start: range.start.clone(), end: range.end.clone() })
    })

  return mergedRanges
}

function getMonthCoverageStatus(monthStart, monthEnd, ranges) {
  const overlappingRanges = ranges.filter(
    range =>
      !range.isReversed &&
      !range.end.isBefore(monthStart) &&
      !range.start.isAfter(monthEnd)
  )

  if (overlappingRanges.length === 0) {
    return 'missing'
  }

  const mergedRanges = mergeReportRanges(overlappingRanges)
  const isFullyCovered = mergedRanges.some(
    range => !range.start.isAfter(monthStart) && !range.end.isBefore(monthEnd)
  )

  return isFullyCovered ? 'covered' : 'partial'
}

/**
 * Builds a month-by-month view for the expected six-year historical window.
 */
export function buildMonthlyCoverage(ranges, referenceDate) {
  const expectedStart = referenceDate
    .clone()
    .startOf('day')
    .subtract(EXPECTED_HISTORY_YEARS, 'years')
  const expectedEnd = referenceDate.clone().startOf('day')
  const currentMonth = expectedStart.clone().startOf('month')
  const lastMonth = expectedEnd.clone().startOf('month')
  const months = []

  while (!currentMonth.isAfter(lastMonth)) {
    const monthStart = moment.max(currentMonth.clone().startOf('month'), expectedStart)
    const monthEnd = moment.min(currentMonth.clone().endOf('month'), expectedEnd)

    months.push({
      key: currentMonth.format('YYYY-MM'),
      year: currentMonth.year(),
      month: currentMonth.month(),
      label: currentMonth.format('MMM'),
      fullLabel: currentMonth.format('MMMM YYYY'),
      status: getMonthCoverageStatus(monthStart, monthEnd, ranges)
    })

    currentMonth.add(1, 'month')
  }

  return months
}

export function findCoverageGaps(ranges) {
  const gaps = []
  let coverageEnd = null

  ranges
    .filter(range => !range.isReversed)
    .forEach(range => {
      if (coverageEnd) {
        const gapDays = range.start.diff(coverageEnd, 'days')

        if (gapDays > ALLOWED_GAP_DAYS) {
          gaps.push({
            from: coverageEnd.clone(),
            to: range.start.clone(),
            days: gapDays
          })
        }
      }

      if (!coverageEnd || range.end.isAfter(coverageEnd)) {
        coverageEnd = range.end.clone()
      }
    })

  return gaps
}

/**
 * Rooms sold over a period divided by the average stay length, limited by the occupancy
 * a property realistically reaches.
 */
export function estimateExpectedBookings({ numberOfRooms, coveredDays }) {
  if (!numberOfRooms || numberOfRooms <= 0 || !coveredDays || coveredDays <= 0) {
    return null
  }

  return (numberOfRooms * coveredDays * EXPECTED_OCCUPANCY_RATE) / AVERAGE_BOOKING_NIGHTS
}

/**
 * Compares each report on its own, the way a CSM reads the upload log row by row.
 */
export function buildReportEstimates(ranges, numberOfRooms) {
  return ranges.map(range => {
    const expectedBookings = estimateExpectedBookings({
      numberOfRooms,
      coveredDays: range.coveredDays
    })

    if (!expectedBookings) {
      return {
        id: range.id,
        coveredDays: range.coveredDays,
        bookings: range.bookings,
        expectedBookings: null,
        ratio: null,
        status: ONBOARDING_CHECK_STATUSES.unknown,
        message:
          'The number of rooms is not set for this hotel, so the expected booking volume cannot be estimated.'
      }
    }

    const ratio = range.bookings / expectedBookings
    const isOutsideExpectedRange = ratio < LOW_VOLUME_RATIO || ratio > HIGH_VOLUME_RATIO

    return {
      id: range.id,
      coveredDays: range.coveredDays,
      bookings: range.bookings,
      expectedBookings,
      ratio,
      status: isOutsideExpectedRange
        ? ONBOARDING_CHECK_STATUSES.warning
        : ONBOARDING_CHECK_STATUSES.ok,
      message: `${formatDate(range.start)} - ${formatDate(range.end)} covers ${pluralize(
        range.coveredDays,
        'day'
      )} and should bring about ${formatCount(
        expectedBookings
      )} bookings, but ${formatCount(range.bookings)} arrived.`
    }
  })
}

function buildHistoryStartCheck(ranges, referenceDate) {
  const title = 'Historical data start'

  if (ranges.length === 0) {
    return {
      key: ONBOARDING_CHECK_KEYS.historyStart,
      status: ONBOARDING_CHECK_STATUSES.unknown,
      title,
      message: 'No report with a data date range was found for the selected period.'
    }
  }

  const expectedStart = referenceDate
    .clone()
    .startOf('day')
    .subtract(EXPECTED_HISTORY_YEARS, 'years')
  const historyStart = ranges[0].start

  if (historyStart.diff(expectedStart, 'days') <= MISSING_HISTORY_TOLERANCE_DAYS) {
    return {
      key: ONBOARDING_CHECK_KEYS.historyStart,
      status: ONBOARDING_CHECK_STATUSES.ok,
      title,
      message: `Data starts on ${formatDate(
        historyStart
      )} and covers the expected ${EXPECTED_HISTORY_YEARS} years.`
    }
  }

  const missingMonths = Math.max(1, historyStart.diff(expectedStart, 'months'))

  return {
    key: ONBOARDING_CHECK_KEYS.historyStart,
    status: ONBOARDING_CHECK_STATUSES.warning,
    title,
    message: `Data starts on ${formatDate(
      historyStart
    )} but ${EXPECTED_HISTORY_YEARS} years back to ${formatDate(
      expectedStart
    )} is expected, so about ${pluralize(missingMonths, 'month')} ${
      missingMonths === 1 ? 'is' : 'are'
    } missing.`
  }
}

function buildDateOrderCheck(ranges) {
  const title = 'Date order and gaps'

  if (ranges.length === 0) {
    return {
      key: ONBOARDING_CHECK_KEYS.dateOrder,
      status: ONBOARDING_CHECK_STATUSES.unknown,
      title,
      message: 'No report with a data date range was found for the selected period.'
    }
  }

  const reversedRanges = ranges.filter(range => range.isReversed)
  const gaps = findCoverageGaps(ranges)

  if (reversedRanges.length === 0 && gaps.length === 0) {
    const coverageEnd = ranges.reduce(
      (latest, range) => (range.end.isAfter(latest) ? range.end : latest),
      ranges[0].end
    )

    return {
      key: ONBOARDING_CHECK_KEYS.dateOrder,
      status: ONBOARDING_CHECK_STATUSES.ok,
      title,
      message: `${pluralize(ranges.length, 'report')} cover ${formatDate(
        ranges[0].start
      )} - ${formatDate(coverageEnd)} in order and without gaps.`
    }
  }

  const messages = []

  if (reversedRanges.length > 0) {
    messages.push(
      reversedRanges.length === 1
        ? '1 report ends before it begins.'
        : `${reversedRanges.length} reports end before they begin.`
    )
  }

  if (gaps.length > 0) {
    const largestGap = gaps.reduce(
      (largest, gap) => (gap.days > largest.days ? gap : largest),
      gaps[0]
    )

    messages.push(
      `${pluralize(gaps.length, 'gap')} found, the largest one is ${pluralize(
        largestGap.days,
        'day'
      )} between ${formatDate(largestGap.from)} and ${formatDate(largestGap.to)}.`
    )
  }

  return {
    key: ONBOARDING_CHECK_KEYS.dateOrder,
    status: ONBOARDING_CHECK_STATUSES.warning,
    title,
    message: messages.join(' ')
  }
}

function buildDataVolumeCheck({
  ranges,
  numberOfRooms,
  coveredDays,
  totalBookings,
  reportEstimates
}) {
  const title = 'Booking volume estimate'

  if (ranges.length === 0) {
    return {
      key: ONBOARDING_CHECK_KEYS.dataVolume,
      status: ONBOARDING_CHECK_STATUSES.unknown,
      title,
      message: 'No report with a data date range was found for the selected period.'
    }
  }

  const expectedBookings = estimateExpectedBookings({ numberOfRooms, coveredDays })

  if (!expectedBookings) {
    return {
      key: ONBOARDING_CHECK_KEYS.dataVolume,
      status: ONBOARDING_CHECK_STATUSES.unknown,
      title,
      message:
        'The number of rooms is not set for this hotel, so the expected booking volume cannot be estimated.'
    }
  }

  const ratio = totalBookings / expectedBookings
  const ratioPercentage = Math.round(ratio * 100)
  const coverageSentence = `${pluralize(coveredDays, 'day')} of data with ${pluralize(
    numberOfRooms,
    'room'
  )} should bring about ${formatCount(expectedBookings)} bookings and ${formatCount(
    totalBookings
  )} arrived.`
  const numberOfOutlierReports = reportEstimates.filter(
    estimate => estimate.status === ONBOARDING_CHECK_STATUSES.warning
  ).length
  const outlierSentence =
    numberOfOutlierReports > 0
      ? ` ${numberOfOutlierReports} of ${pluralize(
          reportEstimates.length,
          'report'
        )} are outside the expected range.`
      : ''

  if (ratio < LOW_VOLUME_RATIO) {
    return {
      key: ONBOARDING_CHECK_KEYS.dataVolume,
      status: ONBOARDING_CHECK_STATUSES.warning,
      title,
      message: `${coverageSentence} That is only ${ratioPercentage}% of the estimate, so data is likely missing.${outlierSentence}`
    }
  }

  if (ratio > HIGH_VOLUME_RATIO) {
    return {
      key: ONBOARDING_CHECK_KEYS.dataVolume,
      status: ONBOARDING_CHECK_STATUSES.warning,
      title,
      message: `${coverageSentence} That is ${ratioPercentage}% of the estimate and above the ${Math.round(
        INDUSTRY_MAX_OCCUPANCY_RATE * 100
      )}% occupancy the industry reaches at most, so the reports may repeat records.${outlierSentence}`
    }
  }

  if (numberOfOutlierReports > 0) {
    return {
      key: ONBOARDING_CHECK_KEYS.dataVolume,
      status: ONBOARDING_CHECK_STATUSES.warning,
      title,
      message: `${coverageSentence} The total is ${ratioPercentage}% of the estimate, but${outlierSentence}`
    }
  }

  return {
    key: ONBOARDING_CHECK_KEYS.dataVolume,
    status: ONBOARDING_CHECK_STATUSES.ok,
    title,
    message: `${coverageSentence} That is ${ratioPercentage}% of the estimate, which is a healthy volume.`
  }
}

/**
 * Verifies the onboarding data a hotel has uploaded so far.
 *
 * @param {{
 *   logs?: object[],
 *   numberOfRooms?: number | null,
 *   now?: moment.Moment | string
 * }} input
 */
export function verifyOnboardingData({
  logs = [],
  numberOfRooms = null,
  now = moment()
}) {
  const referenceDate = moment(now)
  const ranges = collectReportRanges(logs)
  const mergedRanges = mergeReportRanges(ranges)
  const coveredDays = mergedRanges.reduce(
    (total, range) => total + Math.max(1, range.end.diff(range.start, 'days')),
    0
  )
  const totalBookings = ranges.reduce((total, range) => total + range.bookings, 0)
  const reportEstimates = buildReportEstimates(ranges, numberOfRooms)
  const monthlyCoverage = buildMonthlyCoverage(ranges, referenceDate)

  const checks = [
    buildHistoryStartCheck(ranges, referenceDate),
    buildDateOrderCheck(ranges),
    buildDataVolumeCheck({
      ranges,
      numberOfRooms,
      coveredDays,
      totalBookings,
      reportEstimates
    })
  ]

  return {
    coveredDays,
    totalBookings,
    numberOfReports: ranges.length,
    expectedBookings: estimateExpectedBookings({ numberOfRooms, coveredDays }),
    hasWarnings: checks.some(check => check.status === ONBOARDING_CHECK_STATUSES.warning),
    checks,
    monthlyCoverage,
    reportEstimates,
    reportEstimatesById: reportEstimates.reduce(
      (estimatesById, estimate) => ({ ...estimatesById, [estimate.id]: estimate }),
      {}
    )
  }
}
